import api from '../client'

const segment = (value) => encodeURIComponent(value)

// Content moderation: the organization review queue, phrase rules and the
// rule tester, the brand's moderation state, Command Center overrides and
// exemption revocation, the per-organization switch and the public label
// scan.
export const moderationApi = {
  listReviews: async (orgSlug, params = {}) => {
    const response = await api.get(`/${orgSlug}/moderation/reviews`, { params })
    return response.data
  },
  getReview: async (orgSlug, reviewId) => {
    const response = await api.get(`/${orgSlug}/moderation/reviews/${segment(reviewId)}`)
    return response.data
  },
  // reviewMediaUrl turns a review media content_url (a path under the admin
  // API, carrying its own short-lived ticket) into a URL an <img> or
  // <video> element can load without the bearer header.
  reviewMediaUrl: (contentUrl) => (contentUrl ? `${api.defaults.baseURL}${contentUrl}` : ''),
  approveReview: async (orgSlug, reviewId, decision) => {
    const response = await api.post(`/${orgSlug}/moderation/reviews/${segment(reviewId)}/approve`, decision)
    return response.data
  },
  rejectReview: async (orgSlug, reviewId, decision) => {
    const response = await api.post(`/${orgSlug}/moderation/reviews/${segment(reviewId)}/reject`, decision)
    return response.data
  },
  // overturnReview approves a review a brand admin rejected; decision is
  // the decisionPayload of the rejected review plus a required reason.
  overturnReview: async (orgSlug, reviewId, decision) => {
    const response = await api.post(`/${orgSlug}/moderation/reviews/${segment(reviewId)}/overturn`, decision)
    return response.data
  },
  // getModerationStatus says whether moderation applies to the brand now:
  // platform_mode, org_enabled, enforcing and flagged_content.
  getModerationStatus: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/moderation/status`)
    return response.data
  },
  listPhraseRules: async (orgSlug) => {
    const response = await api.get(`/${orgSlug}/moderation/phrase-rules`)
    return response.data
  },
  createPhraseRule: async (orgSlug, rule) => {
    const response = await api.post(`/${orgSlug}/moderation/phrase-rules`, rule)
    return response.data
  },
  // patch carries expected_version: the server refuses an edit of a rule
  // that changed since it was read.
  updatePhraseRule: async (orgSlug, ruleId, patch) => {
    const response = await api.patch(`/${orgSlug}/moderation/phrase-rules/${segment(ruleId)}`, patch)
    return response.data
  },
  deletePhraseRule: async (orgSlug, ruleId, expectedVersion) => {
    const response = await api.delete(`/${orgSlug}/moderation/phrase-rules/${segment(ruleId)}`, {
      params: { expected_version: expectedVersion },
    })
    return response.data
  },
  // previewPhraseRule says what a rule create, update or delete would do to
  // the brand's held and rule-rejected posts. It writes nothing.
  previewPhraseRule: async (orgSlug, change) => {
    const response = await api.post(`/${orgSlug}/moderation/phrase-rules/preview`, change)
    return response.data
  },
  // testModeration matches text against the organization's current rules.
  // It writes nothing and calls no provider.
  testModeration: async (orgSlug, content) => {
    const response = await api.post(`/${orgSlug}/moderation/test`, content)
    return response.data
  },
  overridePost: async (orgSlug, postId, override) => {
    const response = await api.post(`/${orgSlug}/posts/${segment(postId)}/moderation/override`, override)
    return response.data
  },
  revokeExemption: async (orgSlug, exemptionId, reason) => {
    const response = await api.post(`/${orgSlug}/moderation/exemptions/${segment(exemptionId)}/revoke`, { reason })
    return response.data
  },
  listOrganizations: async () => {
    const response = await api.get('/command-center/moderation/organizations')
    return response.data
  },
  setOrganizationModeration: async (organizationId, change) => {
    const response = await api.patch(`/command-center/organizations/${segment(organizationId)}/content-moderation`, change)
    return response.data
  },
  // setOrganizationFlaggedContent changes what a flag does for an
  // organization whose moderation is on. The answer carries reviews_flipped.
  setOrganizationFlaggedContent: async (organizationId, humanReviewRequired) => {
    const response = await api.patch(`/command-center/organizations/${segment(organizationId)}/content-moderation`, {
      human_review_required: humanReviewRequired,
    })
    return response.data
  },
  // listLabelMatches scans every brand's public labels (tags, creator names,
  // call-to-action links, caption labels) against the phrase rules now.
  listLabelMatches: async () => {
    const response = await api.get('/command-center/moderation/label-matches')
    return response.data
  },
}
